import  { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Eye, Target, Phone, Globe, Navigation, CalendarCheck,
  TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import { OverviewData } from "../types/PerformanceTypes";
import { C, METRIC_COLORS, METRIC_LABELS } from "../../../constants/constants";
import { buildSeries, fmtValue, tickFmt } from "../utils/PerformanceUtils";

const IMP_KEYS = [
  "BUSINESS_IMPRESSIONS_DESKTOP_MAPS",
  "BUSINESS_IMPRESSIONS_DESKTOP_SEARCH",
  "BUSINESS_IMPRESSIONS_MOBILE_MAPS",
  "BUSINESS_IMPRESSIONS_MOBILE_SEARCH",
];

 
const DeltaBadge = ({ current, previous }: { current: number; previous: number }) => {
  if (!previous) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  const up  = pct > 0;
  const eq  = pct === 0;
  if (eq) return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
      <Minus size={9} strokeWidth={2.5} /> 0%
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold
      ${up ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-rose-50 text-rose-600 ring-1 ring-rose-200"}`}>
      {up ? <TrendingUp size={9} strokeWidth={2.5} /> : <TrendingDown size={9} strokeWidth={2.5} />}
      {up ? "+" : ""}{pct}%
    </span>
  );
};

 
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const primary = payload.filter((p: any) => !p.dataKey?.startsWith("cmp_"));
  return (
    <div className="bg-white border border-slate-100 rounded-xl shadow-lg p-3 min-w-[180px] text-xs">
      <p className="font-semibold text-slate-700 border-b border-slate-100 pb-2 mb-2">{label}</p>
      <div className="space-y-1.5">
        {primary.map((entry: any) => (
          <div key={entry.dataKey} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-slate-500">
              <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: entry.color }} />
              {METRIC_LABELS[entry.dataKey] || entry.name}
            </span>
            <span className="font-semibold text-slate-800">{fmtValue(entry.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

 
const PeriodPill = ({ label, variant }: { label: string; variant: "a" | "b" }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold
    ${variant === "a" ? "bg-blue-50 text-blue-700" : "bg-violet-50 text-violet-600"}`}>
    <span className={`w-4 border-t-[2px] inline-block flex-shrink-0
      ${variant === "b" ? "border-dashed border-violet-400" : "border-blue-500"}`} />
    {label}
  </span>
);

 
interface SourceBarProps {
  name:               string;
  value:              number;
  prevValue?:         number;
  maxValue:           number;
  color:              string;
  compareEnabled?:    boolean;
  comparePeriodLabel?: string;
}

const SourceBar = ({ name, value, prevValue, maxValue, color, compareEnabled, comparePeriodLabel }: SourceBarProps) => {
  const fillW = Math.round((value / maxValue) * 100);
  const prevW = prevValue != null ? Math.round((prevValue / maxValue) * 100) : 0;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium text-slate-600 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: color }} />
          {name}
        </span>
        <span className="flex items-center gap-2">
          {compareEnabled && prevValue != null && <DeltaBadge current={value} previous={prevValue} />}
          <span className="text-[12px] font-semibold text-slate-800">{fmtValue(value)}</span>
        </span>
      </div>
      <div className="relative h-1.5 bg-slate-100 rounded-full overflow-hidden">
        {compareEnabled && prevValue != null && (
          <div className="absolute inset-y-0 left-0 h-full rounded-full opacity-30 transition-[width] duration-500"
               style={{ width: `${prevW}%`, background: color }} />
        )}
        <div className="absolute inset-y-0 left-0 h-full rounded-full transition-[width] duration-700"
             style={{ width: `${fillW}%`, background: color }} />
      </div>
      {compareEnabled && prevValue != null && (
        <p className="text-[10px] text-slate-400 leading-none">
          vs <span className="font-semibold text-slate-500">{fmtValue(prevValue)}</span>
          {comparePeriodLabel && <span className="text-violet-400 ml-1">{comparePeriodLabel}</span>}
        </p>
      )}
    </div>
  );
};

 
interface OverviewTabProps {
  data:                OverviewData;
  compareEnabled?:     boolean;
  compareData?:        OverviewData;
  primaryPeriodLabel?:  string;
  comparePeriodLabel?:  string;
}

 
const OverviewTab = ({
  data,
  compareEnabled,
  compareData,
  primaryPeriodLabel = "Période A",
  comparePeriodLabel = "Période B",
}: OverviewTabProps) => {
  const { kpis, series } = data;

  // Merged area chart data
  const chartData = useMemo(() => {
    const primary = buildSeries(series, IMP_KEYS);
    if (!compareEnabled || !compareData) return primary;
    const cmp = buildSeries(compareData.series, IMP_KEYS);
    const maxLen = Math.max(primary.length, cmp.length);
    return Array.from({ length: maxLen }, (_, i) => ({
      ...(primary[i] || { date: `J+${i}` }),
      ...Object.fromEntries(IMP_KEYS.map(k => [`cmp_${k}`, cmp[i]?.[k] ?? null])),
    }));
  }, [series, compareData, compareEnabled]);

  // Source items derived from series totals
  const sourceItems = useMemo(() => {
    const sumSeries = (src: typeof series, key: string) =>
      (src[key] || []).reduce((a: number, p: any) => a + p.value, 0);
    return IMP_KEYS.map(k => ({
      key:   k,
      name:  METRIC_LABELS[k] || k,
      value: sumSeries(series, k),
      prev:  compareData ? sumSeries(compareData.series, k) : undefined,
      color: METRIC_COLORS[k] || C.primary,
    })).sort((a, b) => b.value - a.value);
  }, [series, compareData]);

  const sourceMax = sourceItems[0]?.value || 1;
  const topCanal  = sourceItems[0]?.name ?? "—";
  const gradIds   = IMP_KEYS.map((_, i) => `impGrad${i}`);

  const kpiItems = [
    { label: "Impressions",  value: kpis.totalImpressions,  prev: compareData?.kpis.totalImpressions,  icon: Eye,           color: C.primary   },
    { label: "Actions",      value: kpis.totalActions,      prev: compareData?.kpis.totalActions,      icon: Target,        color: C.success   },
    { label: "Appels",       value: kpis.callClicks,        prev: compareData?.kpis.callClicks,        icon: Phone,         color: C.accent    },
    { label: "Site web",     value: kpis.websiteClicks,     prev: compareData?.kpis.websiteClicks,     icon: Globe,         color: C.secondary },
   ];

  return (
    <div className="flex flex-col gap-6 animate-[gbpFadeIn_0.2s_ease]">

       <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpiItems.map(({ label, value, prev, icon: Icon, color }) => (
          <div key={label}
               className="relative bg-white rounded-2xl border border-slate-100 p-4 flex flex-col gap-2.5
                          hover:border-slate-200 hover:shadow-sm transition-all duration-150">
            <div className="flex items-start justify-between">
              <div className="w-8 h-8 rounded-[9px] flex items-center justify-center flex-shrink-0"
                   style={{ background: `${color}18`, color }}>
                <Icon size={15} strokeWidth={2} />
              </div>
              {compareEnabled && prev !== undefined && (
                <DeltaBadge current={value} previous={prev} />
              )}
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
              <p className="text-[22px] font-bold text-slate-900 leading-none tracking-tight">{fmtValue(value)}</p>
              {compareEnabled && prev !== undefined && (
                <p className="text-[10px] text-slate-400 mt-1.5">
                  vs <span className="font-semibold text-slate-500">{fmtValue(prev)}</span>
                  <span className="text-violet-400 ml-1 text-[9px]">{comparePeriodLabel}</span>
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

       <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">

         <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-[13px] font-semibold text-slate-800">Évolution des impressions</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Trafic journalier par canal</p>
            </div>
            {compareEnabled && (
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <PeriodPill label={primaryPeriodLabel} variant="a" />
                <PeriodPill label={comparePeriodLabel} variant="b" />
              </div>
            )}
          </div>

          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <defs>
                {IMP_KEYS.map((k, i) => (
                  <linearGradient key={k} id={gradIds[i]} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={METRIC_COLORS[k] || C.primary} stopOpacity={0.12} />
                    <stop offset="95%" stopColor={METRIC_COLORS[k] || C.primary} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="date" axisLine={false} tickLine={false}
                     tick={{ fontSize: 10, fill: "#94a3b8" }} dy={8} />
              <YAxis axisLine={false} tickLine={false}
                     tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={tickFmt} />
              <Tooltip content={<CustomTooltip />} />
              {IMP_KEYS.map((k, i) => (
                <Area key={k} type="monotone" dataKey={k}
                      stroke={METRIC_COLORS[k] || C.primary} strokeWidth={2}
                      fill={`url(#${gradIds[i]})`}
                      dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
              ))}
              {compareEnabled && IMP_KEYS.map((k) => (
                <Area key={`cmp_${k}`} type="monotone" dataKey={`cmp_${k}`}
                      stroke={METRIC_COLORS[k] || C.primary} strokeWidth={1.5}
                      strokeDasharray="4 3" strokeOpacity={0.4}
                      fill="none" dot={false} legendType="none" />
              ))}
            </AreaChart>
          </ResponsiveContainer>

           <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-50">
            {IMP_KEYS.map((k) => (
              <span key={k} className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                      style={{ background: METRIC_COLORS[k] || C.primary }} />
                {METRIC_LABELS[k]}
              </span>
            ))}
          </div>
        </div>

         <div className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col">
          <p className="text-[13px] font-semibold text-slate-800 mb-0.5">Répartition des sources</p>
          <p className="text-[11px] text-slate-400 mb-5">Où vos clients vous trouvent</p>

          <div className="flex flex-col gap-4 flex-1">
            {sourceItems.map((s) => (
              <SourceBar
                key={s.key}
                name={s.name}
                value={s.value}
                prevValue={s.prev}
                maxValue={sourceMax}
                color={s.color}
                compareEnabled={compareEnabled}
                comparePeriodLabel={comparePeriodLabel}
              />
            ))}
          </div>

          <div className="flex items-center justify-between mt-5 pt-3 border-t border-slate-100">
            <span className="text-[11px] text-slate-400 font-medium">Canal principal</span>
            <span className="text-[12px] font-semibold text-slate-800">{topCanal}</span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default OverviewTab;